import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import ChevronIcon from './chevron_icon';
import { STAR_PULSE_MS, SEARCH_DROPDOWN_MAX_HEIGHT } from '../constants'
import { pickTranslatedName } from '../utils/events_utils';
import { capHeightToFrame } from '../utils/viewport_utils'
import { componentText } from '../utils/i18n_utils'

const t = (key, fallback, params) => componentText(`member_search.${key}`, fallback, params)

const MemberSearchDropdown = ({
  results,
  loading,
  isQueryTooShort,
  minQueryLength,
  expandedMemberId,
  aggregates,
  aggregatesLoading,
  favoriteMemberIds,
  onToggleMemberFavorite,
  onExpandMember,
  onCollapseMember,
  onSelectAggregate,
}) => {
  const [collapsedAggregates, setCollapsedAggregates] = useState({ memberId: null, eventIds: [] });

  const dropdownRef = useRef(null)

  useLayoutEffect(() => {
    capHeightToFrame(dropdownRef.current, SEARCH_DROPDOWN_MAX_HEIGHT)
  })

  // Same click-only pulse the leaderboard star uses; a rule on the filled
  // class would fire for every already-favourited member on open.
  const [ pulsingMemberId, setPulsingMemberId ] = useState(null)

  useEffect(() => {
    if (!pulsingMemberId) {
      return undefined
    }

    const timer = setTimeout(() => setPulsingMemberId(null), STAR_PULSE_MS)
    return () => clearTimeout(timer)
  }, [ pulsingMemberId ])

  // The row star favourites its own event synchronously; this one has no
  // event context, so the store only catches up once the played-events
  // fan-out returns - a round trip, unless that member happens to be
  // expanded. Hold the flip locally so the star responds on click.
  const [ optimisticFavorites, setOptimisticFavorites ] = useState({})

  const handleToggleFavorite = (memberId) => {
    const id = String(memberId)
    setPulsingMemberId(memberId)
    setOptimisticFavorites((prev) => ({ ...prev, [id]: !favoriteMemberIds.has(memberId) }))
    onToggleMemberFavorite(memberId)
  }

  // Drop each override once the store agrees, so a rejected toggle cannot
  // leave the star stuck in the state the click assumed.
  useEffect(() => {
    setOptimisticFavorites((prev) => {
      const settled = Object.keys(prev).filter((id) => favoriteMemberIds.has(id) === prev[id])
      if (settled.length === 0) {
        return prev
      }

      const next = { ...prev }
      settled.forEach((id) => delete next[id])
      return next
    })
  }, [ favoriteMemberIds ])

  // All of the expanded member's events start open; only events the user has
  // collapsed (for this member) are closed. Multiple can be open at once.
  const collapsedEventIds = collapsedAggregates.memberId === expandedMemberId
    ? collapsedAggregates.eventIds
    : [];
  const isAggregateExpanded = (eventId) => !collapsedEventIds.includes(eventId);

  const handleMemberClick = (memberId) => {
    if (expandedMemberId === memberId) {
      onCollapseMember();
    } else {
      onExpandMember(memberId);
    }
  };

  const handleToggleAggregateDetails = (eventId) => {
    setCollapsedAggregates((prev) => {
      const current = prev.memberId === expandedMemberId ? prev.eventIds : [];
      const eventIds = current.includes(eventId)
        ? current.filter((id) => id !== eventId)
        : [...current, eventId];
      return { memberId: expandedMemberId, eventIds };
    });
  };

  return (
    <ul className="lbv2__member-search-dropdown" ref={dropdownRef}>
      {isQueryTooShort && (
        <li className="lbv2__member-search-hint">
          {t('type_at_least', `Type at least ${minQueryLength} characters`, { min: minQueryLength })}
        </li>
      )}
      {!isQueryTooShort && loading && (
        <li className="lbv2__member-search-loading">{t('searching', 'Searching...')}</li>
      )}
      {!isQueryTooShort && !loading && results.length === 0 && (
        <li className="lbv2__member-search-empty">{t('no_results', 'No results')}</li>
      )}
      {!isQueryTooShort && !loading && results.map((member) => {
        const isMemberExpanded = expandedMemberId === member.member_id;
        const optimistic = optimisticFavorites[String(member.member_id)]
        const isFavorite = optimistic === undefined ? favoriteMemberIds.has(member.member_id) : optimistic
        return (
          <li key={member.member_id} className="lbv2__member-search-event-group">
            <div className="lbv2__member-search-event-header">
              <button
                className={`lbv2__member-search-star fa-star fa${isFavorite ? 's is-favorite' : 'r'} ${pulsingMemberId === member.member_id ? 'is-pulsing' : ''} themed-text`}
                onClick={() => handleToggleFavorite(member.member_id)}
                aria-label={isFavorite ? t('remove_favorite', 'Remove from favorites') : t('add_favorite', 'Add to favorites')}
              />
              <button
                className="lbv2__member-search-member-btn"
                onClick={() => handleMemberClick(member.member_id)}
              >
                <span className="lbv2__member-search-member-label">
                  <span className="lbv2__member-search-member-name">{member.name}</span>
                  {member.descriptor && (
                    <span className="lbv2__member-search-member-descriptor">{member.descriptor}</span>
                  )}
                </span>
                <ChevronIcon isOpen={isMemberExpanded} />
              </button>
            </div>
            {isMemberExpanded && (
              <ul className="lbv2__member-search-players">
                {aggregatesLoading && (
                  <li className="lbv2__member-search-loading">{t('loading', 'Loading...')}</li>
                )}
                {!aggregatesLoading && aggregates.map((agg) => {
                  const isAggExpanded = isAggregateExpanded(agg.event_id);
                  return (
                    <li key={agg.event_id} className="lbv2__member-search-option">
                      <button
                        className="lbv2__member-search-option-header"
                        onClick={() => handleToggleAggregateDetails(agg.event_id)}
                        aria-expanded={isAggExpanded}
                      >
                        <ChevronIcon isOpen={isAggExpanded} />
                        <span className="lbv2__member-search-option-name">
                          {pickTranslatedName(agg.name_translations, agg.event_name)}
                        </span>
                      </button>
                      {isAggExpanded && (
                        <button
                          className="lbv2__member-search-agg-details"
                          onClick={() => onSelectAggregate(agg, member.member_id)}
                        >
                          {agg.rank_display && (
                            <span className="lbv2__member-search-agg-rank">{agg.rank_display}.</span>
                          )}
                          {agg.rank_value_display && (
                            <span className="lbv2__member-search-agg-stat">
                              <span className="lbv2__member-search-agg-stat-label">{t('total_short', 'TOT')}</span>
                              <span className="lbv2__member-search-agg-stat-value">{agg.rank_value_display}</span>
                            </span>
                          )}
                          {agg.thru_display && (
                            <span className="lbv2__member-search-agg-stat">
                              <span className="lbv2__member-search-agg-stat-label">{t('thru_short', 'THRU')}</span>
                              <span className="lbv2__member-search-agg-stat-value lbv2__member-search-agg-stat-value--plain">{agg.thru_display}</span>
                            </span>
                          )}
                          {agg.last_round_score && agg.last_round_label && (
                            <span className="lbv2__member-search-agg-stat">
                              <span className="lbv2__member-search-agg-stat-label">{agg.last_round_label}</span>
                              <span className="lbv2__member-search-agg-stat-value">{agg.last_round_score}</span>
                              {agg.last_round_course && (
                                <span className="lbv2__member-search-agg-stat-course">{agg.last_round_course}</span>
                              )}
                            </span>
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

MemberSearchDropdown.propTypes = {
  isQueryTooShort: PropTypes.bool,
  minQueryLength: PropTypes.number,
  results: PropTypes.arrayOf(PropTypes.shape({
    member_id: PropTypes.string,
    name: PropTypes.string,
    descriptor: PropTypes.string,
  })),
  loading: PropTypes.bool,
  expandedMemberId: PropTypes.string,
  aggregates: PropTypes.arrayOf(PropTypes.shape({
    event_id: PropTypes.string,
    event_name: PropTypes.string,
    name_translations: PropTypes.objectOf(PropTypes.string),
    aggregate_id: PropTypes.string,
    rank_display: PropTypes.string,
    rank_value_display: PropTypes.string,
    thru_display: PropTypes.string,
    round_id: PropTypes.string,
    last_round_score: PropTypes.string,
    last_round_course: PropTypes.string,
    last_round_label: PropTypes.string,
  })),
  aggregatesLoading: PropTypes.bool,
  favoriteMemberIds: PropTypes.instanceOf(Set),
  onToggleMemberFavorite: PropTypes.func.isRequired,
  onExpandMember: PropTypes.func.isRequired,
  onCollapseMember: PropTypes.func.isRequired,
  onSelectAggregate: PropTypes.func.isRequired,
};

MemberSearchDropdown.defaultProps = {
  isQueryTooShort: false,
  results: [],
  loading: false,
  expandedMemberId: null,
  aggregates: [],
  aggregatesLoading: false,
  favoriteMemberIds: new Set(),
};

export default MemberSearchDropdown;
